import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Phone, Plus, Trash2, Search, Loader2 } from 'lucide-react';
import { apiRequest } from '@/lib/api';

// ─── Types ───

interface PhoneNumber {
  id: string;
  number: string;
  twilio_sid: string | null;
  friendly_name: string | null;
  client_id: string | null;
  client_name: string | null;
  default_agent_id: string | null;
  agent_name: string | null;
  twilio_region: string;
  is_active: boolean;
  created_at: string;
}

interface Agent {
  id: string;
  name: string;
  client_id: string;
}

interface AvailableNumber {
  number: string;
  friendly_name: string;
  region: string;
  locality: string | null;
  capabilities: { voice: boolean; sms: boolean | null };
}

// ─── Helpers ───

function formatPhone(number: string): string {
  // US numbers: +1 (XXX) XXX-XXXX
  if (number.startsWith('+1') && number.length === 12) {
    return `+1 (${number.slice(2, 5)}) ${number.slice(5, 8)}-${number.slice(8)}`;
  }
  return number;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: '2-digit', year: '2-digit',
  });
}

// ─── Component ───

export function PhoneNumbersPage() {
  const [numbers, setNumbers] = useState<PhoneNumber[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Purchase modal
  const [showPurchase, setShowPurchase] = useState(false);
  const [purchaseCountry, setPurchaseCountry] = useState('US');
  const [purchaseAreaCode, setPurchaseAreaCode] = useState('');
  const [purchaseType, setPurchaseType] = useState('local');
  const [purchaseAgentId, setPurchaseAgentId] = useState('');
  const [availableNumbers, setAvailableNumbers] = useState<AvailableNumber[]>([]);
  const [searching, setSearching] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState('');

  // Release confirmation
  const [releaseId, setReleaseId] = useState<string | null>(null);
  const [releasing, setReleasing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [numbersData, agentsData] = await Promise.all([
        apiRequest<PhoneNumber[]>('/phone-numbers'),
        apiRequest<{ agents: Agent[] }>('/studio/agents'),
      ]);
      setNumbers(numbersData);
      setAgents(agentsData.agents || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Reassign Agent ───

  const handleReassign = async (phoneId: string, agentId: string) => {
    try {
      await apiRequest(`/phone-numbers/${phoneId}`, {
        method: 'PATCH',
        body: { default_agent_id: agentId },
      });
      await loadData();
    } catch (e: any) {
      setError(e.message || 'Failed to reassign');
    }
  };

  // ─── Search Available Numbers ───

  const handleSearch = async () => {
    setSearching(true);
    setAvailableNumbers([]);
    setSelectedNumber('');
    try {
      const params = new URLSearchParams({ country: purchaseCountry, type: purchaseType, limit: '10' });
      if (purchaseAreaCode) params.set('area_code', purchaseAreaCode);
      const results = await apiRequest<AvailableNumber[]>(`/phone-numbers/available?${params}`);
      setAvailableNumbers(results);
    } catch (e: any) {
      setError(e.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  // ─── Purchase Number ───

  const handlePurchase = async () => {
    if (!selectedNumber || !purchaseAgentId) return;
    setPurchasing(true);
    try {
      await apiRequest('/phone-numbers/purchase', {
        method: 'POST',
        body: {
          country: purchaseCountry,
          agent_id: purchaseAgentId,
          area_code: purchaseAreaCode || undefined,
          type: purchaseType,
        },
      });
      setShowPurchase(false);
      setAvailableNumbers([]);
      setSelectedNumber('');
      setPurchaseAgentId('');
      setPurchaseAreaCode('');
      await loadData();
    } catch (e: any) {
      setError(e.message || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  // ─── Release Number ───

  const handleRelease = async () => {
    if (!releaseId) return;
    setReleasing(true);
    try {
      await apiRequest(`/phone-numbers/${releaseId}`, { method: 'DELETE' });
      setReleaseId(null);
      await loadData();
    } catch (e: any) {
      setError(e.message || 'Release failed');
    } finally {
      setReleasing(false);
    }
  };

  // ─── Render ───

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  const releaseNumber = numbers.find((n) => n.id === releaseId);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Phone Numbers</h1>
          <p className="text-sm text-muted-foreground">{numbers.length} number{numbers.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => setShowPurchase(true)}>
          <Plus className="mr-2 h-4 w-4" /> Buy Number
        </Button>
      </div>

      {error && (
        <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
          {error}
          <button className="ml-2 underline" onClick={() => setError('')}>dismiss</button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border bg-card">
        <table className="w-full">
          <thead>
            <tr className="border-b text-left text-sm text-muted-foreground">
              <th className="px-4 py-3 font-medium">Number</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Agent</th>
              <th className="px-4 py-3 font-medium">Client</th>
              <th className="px-4 py-3 font-medium">Region</th>
              <th className="px-4 py-3 font-medium">Added</th>
              <th className="px-4 py-3 font-medium w-20"></th>
            </tr>
          </thead>
          <tbody>
            {numbers.map((num) => (
              <tr key={num.id} className="border-b last:border-0 hover:bg-muted/50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="font-mono text-sm">{formatPhone(num.number)}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">{num.friendly_name || '—'}</td>
                <td className="px-4 py-3">
                  <Select
                    value={num.default_agent_id || ''}
                    onValueChange={(val) => handleReassign(num.id, val)}
                  >
                    <SelectTrigger className="h-8 w-48 text-sm">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      {agents.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{num.client_name || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                    num.twilio_region === 'ie1'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                  }`}>
                    {num.twilio_region === 'ie1' ? 'IE1' : 'US1'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {num.created_at ? formatDate(num.created_at) : '—'}
                </td>
                <td className="px-4 py-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setReleaseId(num.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {numbers.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  No phone numbers yet. Click "Buy Number" to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Purchase Modal ─── */}
      <Dialog open={showPurchase} onOpenChange={setShowPurchase}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Buy Phone Number</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Country + Type */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-sm font-medium">Country</label>
                <Select value={purchaseCountry} onValueChange={setPurchaseCountry}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="IL">Israel</SelectItem>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                    <SelectItem value="CA">Canada</SelectItem>
                    <SelectItem value="DE">Germany</SelectItem>
                    <SelectItem value="FR">France</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium">Type</label>
                <Select value={purchaseType} onValueChange={setPurchaseType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="local">Local</SelectItem>
                    <SelectItem value="toll_free">Toll-Free</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Area code + Search */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-sm font-medium">Area Code (optional)</label>
                <Input
                  placeholder="e.g. 415"
                  value={purchaseAreaCode}
                  onChange={(e) => setPurchaseAreaCode(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleSearch} disabled={searching}>
                  {searching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                  Search
                </Button>
              </div>
            </div>

            {/* Available numbers */}
            {availableNumbers.length > 0 && (
              <div>
                <label className="text-sm font-medium">Available Numbers</label>
                <div className="mt-1 max-h-48 overflow-y-auto rounded-md border">
                  {availableNumbers.map((n) => (
                    <button
                      key={n.number}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-muted ${
                        selectedNumber === n.number ? 'bg-primary/10 font-medium' : ''
                      }`}
                      onClick={() => setSelectedNumber(n.number)}
                    >
                      <span className="font-mono">{formatPhone(n.number)}</span>
                      {n.locality && <span className="ml-2 text-muted-foreground">({n.locality})</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Agent selection */}
            {selectedNumber && (
              <div>
                <label className="text-sm font-medium">Assign to Agent</label>
                <Select value={purchaseAgentId} onValueChange={setPurchaseAgentId}>
                  <SelectTrigger><SelectValue placeholder="Select agent..." /></SelectTrigger>
                  <SelectContent>
                    {agents.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchase(false)}>Cancel</Button>
            <Button
              onClick={handlePurchase}
              disabled={!selectedNumber || !purchaseAgentId || purchasing}
            >
              {purchasing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Purchase {selectedNumber ? formatPhone(selectedNumber) : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Release Confirmation ─── */}
      <Dialog open={!!releaseId} onOpenChange={(open) => !open && setReleaseId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Release Phone Number</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will permanently release <strong>{releaseNumber ? formatPhone(releaseNumber.number) : ''}</strong> from
            Twilio and remove it from the system. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReleaseId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRelease} disabled={releasing}>
              {releasing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Release
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
